import { FC, useEffect, useState, useRef, useMemo, useCallback } from "react";
import styles from "./Chat.module.scss";
import { ChatItem } from "./ChatItem";
import { useMessages } from "./Services/useMessages";
import { useAppSelector } from "../../services/hooks";
import { TDialogs } from "../../types/chat";
import { cryptLib } from "./Services/utils";

import searchIcon from "../../images/icons/search_icon.svg";
import reportIcon from "../../images/icons/report.svg";
import deleteIcon from "../../images/icons/delete.svg";
import unblockSvg from "../../images/icons/unblock.svg";
import {
  getDialogs,
  getMessagesForConversation,
  getNewMessage,
} from "../../api/chat";
import { ErrorPage } from "../../pages/ErrorPage/ErrorPage";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmModal, ModalType } from "./ConfrimModal/ConfrimModal";
import { BACKEND_URL, getHeaders } from "../../api/utils";
import Cookies from "js-cookie";
import { socket } from "./Services/socket.service";
import { useNotification } from "../Notification/useNotification";

export const Chat: FC = () => {
  const { user } = useAppSelector((state) => state.user);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogs, setDialogs] = useState<TDialogs[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [pendingConversationId, setPendingConversationId] = useState<
    number | null
  >(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Используем хук для работы с WebSocket
  const {
    messages,
    onlineUsers,
    typingUsers,
    activeConversation,
    sendMessage,
    setTyping,
    joinConversation,
    leaveConversation,
    blockUser,
    unblockUser,
    setBlockStatusChangeHandler,
    setHistoryMessages,
    clearMessages,
    connectionState,
    socketError,
    clearSocketError,
  } = useMessages();
  const { showError } = useNotification();

  // Получаем interlocutorId из URL
  const interlocutorId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    return pathParts.length > 3 ? Number(pathParts[3]) : 0;
  }, [location.pathname]);

  // Фильтруем диалоги по поисковому запросу
  const filteredDialogs = useMemo(() => {
    if (!searchQuery.trim()) return dialogs;
    return dialogs.filter((dialog) =>
      dialog.fullName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [dialogs, searchQuery]);

  // Находим текущий диалог по interlocutorId
  const currentDialog = useMemo(() => {
    return dialogs.find((dialog) => dialog.interlocutorId === interlocutorId);
  }, [dialogs, interlocutorId]);

  // Загрузка списка диалогов
  const fetchDialogs = useCallback(async () => {
    try {
      const response: TDialogs[] = await getDialogs();
      setDialogs(response);
    } catch (err) {
      console.error("Failed to fetch dialogs:", err);
      setError("Failed to load conversations");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Отображаем ошибки сокета как глобальные нотификации
  useEffect(() => {
    if (socketError) {
      showError(socketError.message);
      clearSocketError();
    }
  }, [socketError, showError, clearSocketError]);

  useEffect(() => {
    // if (
    //   !dialogs.find((dialog) => dialog.conversationId === activeConversation)
    // ) {
    fetchDialogs();
    // }

    console.log({ messages });
  }, [messages]);
  // Создание нового диалога если нет существующего
  const createNewConversation = useCallback(async (userId: number) => {
    try {
      setIsLoading(true);
      const newChat = await getNewMessage(userId);
      if (newChat?.conversation?.id) {
        return newChat.conversation.id;
      }
      return null;
    } catch (err) {
      console.error("Failed to create new conversation:", err);
      setError("Failed to create conversation");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Загрузка сообщений для выбранного диалога
  const fetchMessages = useCallback(
    async (userId: number) => {
      if (!userId) return;
      try {
        setIsLoading(true);
        const existingDialog = dialogs.find(
          (dialog) => dialog.interlocutorId === userId,
        );
        let conversationId: number;
        if (existingDialog) {
          conversationId = existingDialog.conversationId;
        } else {
          const newConversationId = await createNewConversation(userId);
          if (!newConversationId) {
            setError("Failed to start conversation");
            setIsLoading(false);
            return;
          }
          conversationId = newConversationId;
        }
        const messagesResponse =
          await getMessagesForConversation(conversationId);
        setHistoryMessages(messagesResponse.messages);
        setPendingConversationId(conversationId); // <-- сохраняем id для подключения
      } catch (err) {
        console.error("Failed to fetch messages:", err);
        setError("Failed to load conversation messages");
      } finally {
        setIsLoading(false);
      }
    },
    [dialogs, createNewConversation, setHistoryMessages],
  );

  useEffect(() => {
    console.log({ connectionState, pendingConversationId });
    if (connectionState.isConnected && pendingConversationId) {
      joinConversation(pendingConversationId);
      setPendingConversationId(null);
    }
  }, [connectionState.isConnected, pendingConversationId]);

  // Обработчик блокировки пользователя
  const handleBlockUser = useCallback(
    (userId: number, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setSelectedUserId(userId);
      setModalType("block");
      setModalOpen(true);
    },
    [],
  );

  // Обработчик разблокировки пользователя
  const handleUnblockUser = useCallback(
    (userId: number, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setSelectedUserId(userId);
      setModalType("unblock");
      setModalOpen(true);
    },
    [],
  );

  // Обработчик удаления диалога
  const handleDeleteDialog = useCallback(
    (userId: number, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setSelectedUserId(userId);
      setModalType("delete");
      setModalOpen(true);
    },
    [],
  );

  // Обработчик подтверждения действия в модальном окне
  const confirmAction = useCallback(() => {
    if (!selectedUserId) {
      setModalOpen(false);
      return;
    }

    switch (modalType) {
      case "block":
        blockUser(selectedUserId);
        fetchDialogs();
        break;
      case "unblock":
        unblockUser(selectedUserId);
        fetchDialogs();
        break;
      case "delete":
        // Находим conversationId для удаления
        const dialogToDelete = dialogs.find(
          (dialog) => dialog.interlocutorId === selectedUserId,
        );
        if (!dialogToDelete) {
          setModalOpen(false);
          return;
        }
        const token = Cookies.get("accessToken");
        if (!token) {
          throw new Error("Authentication token not found");
        }
        fetch(
          `${BACKEND_URL}/chat/conversations/${dialogToDelete.conversationId}`,
          {
            method: "DELETE",
            headers: getHeaders(token),
          },
        )
          .then(() => {
            // Обновляем список диалогов после удаления
            setDialogs((prev) =>
              prev.filter((dialog) => dialog.interlocutorId !== selectedUserId),
            );
            // Если удаляемый диалог был активным, возвращаемся к списку диалогов
            if (interlocutorId === selectedUserId) {
              if (user?.type === "client") {
                navigate("/profile/chat", { replace: true });
              } else if (user?.type === "model") {
                navigate("/model-panel/chat", { replace: true });
              }
            }
          })
          .catch((err) => console.error("Failed to delete conversation:", err));
        break;
    }

    setModalOpen(false);
  }, [
    modalType,
    selectedUserId,
    blockUser,
    unblockUser,
    interlocutorId,
    navigate,
    clearMessages,
  ]);

  // Обработчик изменения статуса блокировки
  useEffect(() => {
    setBlockStatusChangeHandler((userId, isBlocker, isBlocked) => {
      setDialogs((prev) =>
        prev.map((dialog) =>
          dialog.interlocutorId === userId
            ? { ...dialog, isBlocked, isBlocker }
            : dialog,
        ),
      );
    });
  }, [setBlockStatusChangeHandler]);

  // Загрузка диалогов при монтировании
  useEffect(() => {
    if (user && user.type !== "admin") {
      setIsLoading(true);
      fetchDialogs();
    }
  }, [user]);

  // Обработка изменения interlocutorId в URL
  useEffect(() => {
    if (interlocutorId && user) {
      fetchMessages(interlocutorId);
    } else {
      clearMessages();
      leaveConversation();
      setPendingConversationId(null);
    }
  }, [interlocutorId, user]);
  useEffect(() => {
    console.log({ interlocutorId, activeConversation });
    console.log({ interlocutorId, activeConversation });
  }, [interlocutorId, activeConversation]);

  useEffect(() => {
    return () => {
      // Отключаем сокет только при размонтировании Chat
      socket.disconnect();
    };
  }, []);

  if (error || user?.type === "admin") {
    return (
      <ErrorPage message={error || "Chat is not available for admin users."} />
    );
  }

  return (
    <div className={styles.chatContainer}>
      {/* Левая панель со списком диалогов */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Messages</h2>
          <div className={styles.searchBox}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <img src={searchIcon} alt="Search" className={styles.searchIcon} />
          </div>
        </div>

        <div className={styles.dialogsList}>
          {isLoading && filteredDialogs.length === 0 ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loader}>
                <div className={styles.loaderDot}></div>
                <div className={styles.loaderDot}></div>
                <div className={styles.loaderDot}></div>
              </div>
              <p>Loading conversations...</p>
            </div>
          ) : filteredDialogs.length > 0 ? (
            filteredDialogs.map((dialog) => (
              <div
                key={dialog.conversationId}
                className={`${styles.dialogItem} ${interlocutorId === dialog.interlocutorId ? styles.dialogItemActive : ""}`}
                onClick={() => {
                  if (user) {
                    const basePath =
                      user.type === "client"
                        ? "/profile/chat"
                        : "/model-panel/chat";
                    navigate(`${basePath}/${dialog.interlocutorId}`, {
                      replace: true,
                    });
                  }
                }}
              >
                <div className={styles.avatarContainer}>
                  {dialog.avatar ? (
                    <img
                      className={styles.avatar}
                      src={dialog.avatar}
                      alt={dialog.fullName}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      {dialog.fullName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {onlineUsers.includes(dialog.interlocutorId) && (
                    <span
                      className={styles.onlineIndicator}
                      title="Online"
                    ></span>
                  )}
                  {dialog.isBlocked && (
                    <span
                      className={styles.blockedIndicator}
                      title={
                        dialog.isBlocker ? "User blocked" : "You are blocked"
                      }
                    >
                      🚫
                    </span>
                  )}
                </div>

                <div className={styles.dialogContent}>
                  <div className={styles.dialogHeader}>
                    <h3 className={styles.dialogName}>{dialog.fullName}</h3>
                    <span className={styles.dialogTime}>
                      {dialog.lastMessageTime &&
                        new Date(dialog.lastMessageTime).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                    </span>
                  </div>

                  <div className={styles.dialogPreview}>
                    <p className={styles.dialogMessage}>
                      {dialog.isBlocked
                        ? dialog.isBlocker
                          ? "You blocked this user"
                          : "You are blocked"
                        : dialog.lastMessage
                          ? cryptLib.decrypt(dialog.lastMessage)
                          : "No messages yet"}
                    </p>

                    <div className={styles.dialogActions}>
                      {dialog.isBlocked && dialog.isBlocker ? (
                        <button
                          className={styles.actionButton}
                          onClick={(e) =>
                            handleUnblockUser(dialog.interlocutorId, e)
                          }
                          title="Unblock user"
                        >
                          <span className={styles.unblockIcon}>
                            <img src={unblockSvg} alt="Unblock icon" />
                          </span>
                        </button>
                      ) : !dialog.isBlocked ? (
                        <button
                          className={styles.actionButton}
                          onClick={(e) =>
                            handleBlockUser(dialog.interlocutorId, e)
                          }
                          title="Block user"
                        >
                          <img
                            src={reportIcon}
                            alt="Block"
                            className={styles.actionIcon}
                          />
                        </button>
                      ) : null}
                      <button
                        className={styles.actionButton}
                        onClick={(e) =>
                          handleDeleteDialog(dialog.interlocutorId, e)
                        }
                        title="Delete conversation"
                      >
                        <img
                          src={deleteIcon}
                          alt="Delete"
                          className={styles.actionIcon}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyChat}>
              <div className={styles.emptyChatIcon}>💬</div>
              <p>No conversations available</p>
              <p>Start a new chat from a profile page</p>
            </div>
          )}
        </div>
      </div>

      {/* Правая панель с сообщениями */}
      <div className={styles.chatPanel}>
        {interlocutorId ? (
          <ChatItem
            interlocutorId={interlocutorId}
            conversationId={activeConversation}
            messages={messages}
            sendMessage={sendMessage}
            isTyping={
              typingUsers.senderId === interlocutorId && typingUsers.isTyping
            }
            isBlocked={currentDialog?.isBlocked || false}
            isBlocker={currentDialog?.isBlocker || false}
            onlineStatus={onlineUsers.includes(interlocutorId)}
            isConnected={connectionState.isConnected}
            isLoadingDialogs={isLoading}
          />
        ) : (
          <div className={styles.emptyChat}>
            <div className={styles.emptyChatIcon}>👋</div>
            <p>Welcome to your messages</p>
            <p>Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {/* Модальное окно подтверждения */}
      <ConfirmModal
        open={modalOpen}
        type={modalType}
        onCancel={() => setModalOpen(false)}
        onConfirm={confirmAction}
      />
    </div>
  );
};
